import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatDateShort } from "@/lib/formatDate";
import { useApp } from "@/context/AppContext";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import { Plus } from "lucide-react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

export default function Announcements() {
    const { user, restaurants } = useApp();
    const { t } = useTranslation();
    const [items, setItems] = useState([]);
    const [addOpen, setAddOpen] = useState(false);

    const load = async () => {
        const { data } = await api.get("/announcements");
        setItems(data);
    };
    useEffect(() => { load(); }, []);

    const markRead = async (id) => {
        await api.post(`/announcements/${id}/read`);
        toast.success(t("announcements.markedAsRead", "Marked as read"));
    };

    return (
        <div className="px-6 lg:px-12 py-10 max-w-5xl mx-auto">
            <PageHeader
                title={t("announcements.title", "Announcements")}
                subtitle={t("announcements.subtitle", "What does the team need to know?")}
                action={user?.role === "corporate" && (
                    <button data-testid="new-announcement-button"
                        onClick={() => setAddOpen(true)}
                        className="h-10 px-4 inline-flex items-center gap-2 rounded-md bg-neutral-900 text-white text-sm font-semibold hover:bg-neutral-800">
                        <Plus className="h-4 w-4" /> {t("announcements.new", "New")}
                    </button>
                )}
            />
            {items.length === 0 ? (
                <div className="kop-card p-8 text-sm text-neutral-500">{t("announcements.noneYet", "No announcements yet.")}</div>
            ) : (
                <div className="space-y-3">
                    {items.map((a) => (
                        <div key={a.id} data-testid={`announcement-${a.id}`} className="kop-card p-5">
                            <div className="flex items-start justify-between gap-3">
                                <div>
                                    <div className="font-display text-lg font-bold leading-tight">{a.title}</div>
                                    <div className="text-xs text-neutral-500 mt-1">
                                        {a.created_by} · {formatDateShort(a.created_at)}
                                    </div>
                                </div>
                                <StatusBadge status="pending">{t(`announcements.visibility.${a.visibility}`, a.visibility.replace("_", " "))}</StatusBadge>
                            </div>
                            <p className="mt-3 text-sm text-neutral-700 whitespace-pre-line">{a.message}</p>
                            <div className="mt-4 flex items-center gap-2">
                                <button data-testid={`mark-read-${a.id}`}
                                    onClick={() => markRead(a.id)}
                                    className="h-9 px-3 rounded-md border border-neutral-200 text-xs font-semibold hover:bg-neutral-50">
                                    {t("announcements.markAsRead", "Mark as read")}
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
            <AddAnnouncement open={addOpen} onClose={() => setAddOpen(false)}
                onSaved={() => { setAddOpen(false); load(); }} restaurants={restaurants} />
        </div>
    );
}

function AddAnnouncement({ open, onClose, onSaved, restaurants }) {
    const [form, setForm] = useState({ title: "", message: "", visibility: "company", restaurant_id: "" });
    const [saving, setSaving] = useState(false);
    const { t } = useTranslation();
    useEffect(() => {
        if (open) setForm({ title: "", message: "", visibility: "company", restaurant_id: "" });
    }, [open]);

    const save = async () => {
        if (!form.title.trim() || !form.message.trim()) { toast.error(t("announcements.titleMessageRequired", "Title and message required")); return; }
        setSaving(true);
        try {
            const body = { title: form.title, message: form.message, visibility: form.visibility };
            if (form.visibility === "single_restaurant") body.restaurant_id = form.restaurant_id;
            await api.post("/announcements", body);
            toast.success(t("announcements.posted", "Announcement posted"));
            onSaved();
        } finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent data-testid="add-announcement-dialog">
                <DialogHeader>
                    <DialogTitle>{t("announcements.newAnnouncement", "New announcement")}</DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">{t("announcements.fieldTitle", "Title")}</label>
                        <input data-testid="announcement-title" value={form.title}
                            onChange={e => setForm({ ...form, title: e.target.value })}
                            className="mt-1 w-full h-11 px-3 border border-neutral-300 rounded-md" />
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">{t("announcements.fieldMessage", "Message")}</label>
                        <textarea data-testid="announcement-message" rows={5} value={form.message}
                            onChange={e => setForm({ ...form, message: e.target.value })}
                            className="mt-1 w-full px-3 py-2 border border-neutral-300 rounded-md text-sm" />
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">{t("announcements.fieldVisibility", "Visibility")}</label>
                        <Select value={form.visibility} onValueChange={v => setForm({ ...form, visibility: v })}>
                            <SelectTrigger className="mt-1 h-11" data-testid="announcement-visibility"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="company">{t("announcements.allCompany", "All company")}</SelectItem>
                                <SelectItem value="single_restaurant">{t("announcements.singleRestaurant", "Single restaurant")}</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    {form.visibility === "single_restaurant" && (
                        <div>
                            <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">{t("common.restaurant", "Restaurant")}</label>
                            <Select value={form.restaurant_id} onValueChange={v => setForm({ ...form, restaurant_id: v })}>
                                <SelectTrigger className="mt-1 h-11"><SelectValue placeholder={t("announcements.pickRestaurant", "Pick restaurant")} /></SelectTrigger>
                                <SelectContent>
                                    {restaurants.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                    )}
                </div>
                <DialogFooter>
                    <button data-testid="dialog-cancel-button" onClick={onClose} className="h-10 px-4 rounded-md border border-neutral-200 text-sm font-semibold">{t("common.cancel", "Cancel")}</button>
                    <button data-testid="save-announcement" onClick={save} disabled={saving}
                        className="h-10 px-4 rounded-md bg-neutral-900 text-white text-sm font-semibold hover:bg-neutral-800 disabled:opacity-60">
                        {saving ? t("announcements.posting", "Posting…") : t("announcements.post", "Post")}
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
